import React, { useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { HyperFormula } from 'hyperformula';
import researchService from '../../services/researchService';
import { formatNumberFromExcel } from '../../utils/numberFormatUtils';

/**
 * Tables tab for Company Research.
 *
 * Pulls the same XLSX produced by the export endpoint and renders it inline
 * using ExcelJS so we can carry over the spreadsheet's style information
 * (bold, fills, borders, alignment, number formats, merged cells, column
 * widths) into the HTML table. Formula cells are evaluated client-side via
 * HyperFormula so cross-sheet ``SUM`` / ``AVERAGE`` / ``IFERROR`` formulas
 * produce real numbers instead of blank cells.
 */

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

/** ARGB ('FFRRGGBB') -> '#RRGGBB'. Returns null for unset values. */
function argbToHex(argb) {
  if (!argb || typeof argb !== 'string') return null;
  const hex = argb.length === 8 ? argb.slice(2) : argb;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return `#${hex}`;
}

/** Map an openpyxl/exceljs border side spec to a CSS border value. */
function borderToCss(side) {
  if (!side || !side.style) return null;
  const widthMap = { thin: 1, medium: 2, thick: 3, hair: 1 };
  const styleMap = {
    thin: 'solid', medium: 'solid', thick: 'solid', hair: 'solid',
    dotted: 'dotted', dashed: 'dashed', double: 'double',
  };
  const w = widthMap[side.style] ?? 1;
  const s = styleMap[side.style] ?? 'solid';
  const color = argbToHex(side.color?.argb) || '#9aa0a6';
  return `${w}px ${s} ${color}`;
}

/** Convert an ExcelJS cell value to a display string + numeric flag. */
function extractCellValue(cell, evalCtx) {
  const raw = cell.value;
  if (raw === null || raw === undefined) return { display: '', isNumber: false };

  if (typeof raw === 'object' && 'formula' in raw) {
    // Prefer the live HyperFormula evaluation; fall back to whatever value
    // openpyxl happened to cache (almost always nothing for our template).
    let computed = null;
    if (evalCtx && evalCtx.hf && evalCtx.sheetId != null) {
      try {
        computed = evalCtx.hf.getCellValue({
          sheet: evalCtx.sheetId,
          row: evalCtx.row - 1,
          col: evalCtx.col - 1,
        });
      } catch (_err) {
        computed = null;
      }
    }
    // HyperFormula returns DetailedCellError objects for #DIV/0!, #REF!, etc.
    // Treat those as blank so users see the layout rather than error spam.
    if (computed && typeof computed === 'object' && 'type' in computed) {
      computed = null;
    }
    if (typeof computed === 'number' && Number.isFinite(computed)) {
      return { display: formatNumberFromExcel(computed, cell.numFmt), isNumber: true, numericValue: computed };
    }
    if (typeof computed === 'boolean') {
      return { display: computed ? 'TRUE' : 'FALSE', isNumber: false, numericValue: null };
    }
    if (typeof computed === 'string' && computed !== '') {
      return { display: computed, isNumber: false, numericValue: null };
    }
    // Fallback: cached result from the workbook (if any).
    const cached = raw.result;
    if (typeof cached === 'number' && Number.isFinite(cached)) {
      return { display: formatNumberFromExcel(cached, cell.numFmt), isNumber: true, numericValue: cached };
    }
    if (cached != null && cached !== '') {
      return { display: String(cached), isNumber: false, numericValue: null };
    }
    return { display: '', isNumber: false, numericValue: null };
  }

  if (typeof raw === 'object' && Array.isArray(raw.richText)) {
    return { display: raw.richText.map((r) => r.text).join(''), isNumber: false, numericValue: null };
  }
  if (raw instanceof Date) {
    return { display: raw.toISOString().slice(0, 10), isNumber: false, numericValue: null };
  }
  if (typeof raw === 'number') {
    return { display: formatNumberFromExcel(raw, cell.numFmt), isNumber: true, numericValue: raw };
  }
  return { display: String(raw), isNumber: false, numericValue: null };
}

/** Build inline CSS for one cell from its style information. */
function cellStyle(cell, isNumber, numericValue) {
  // Default thin gray borders mimic Excel's gridlines. Explicit cell borders
  // below will override the side(s) they touch.
  const GRID = '1px solid #e5e7eb';
  const style = {
    padding: '2px 6px',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    textAlign: isNumber ? 'right' : 'left',
    borderTop: GRID,
    borderBottom: GRID,
    borderLeft: GRID,
    borderRight: GRID,
  };

  const font = cell.font || {};
  if (font.bold) style.fontWeight = 600;
  if (font.italic) style.fontStyle = 'italic';
  const fontColor = argbToHex(font.color?.argb);
  if (fontColor) {
    style.color = fontColor;
  } else if (typeof numericValue === 'number' && numericValue < 0) {
    // Mirror Excel's accounting format: negative numbers in red
    style.color = '#c00000';
  }
  if (font.size) style.fontSize = `${Number(font.size) || 11}px`;

  const fill = cell.fill;
  if (fill && fill.type === 'pattern' && (fill.pattern === 'solid' || fill.patternType === 'solid')) {
    const bg = argbToHex(fill.fgColor?.argb) || argbToHex(fill.bgColor?.argb);
    if (bg && bg.toLowerCase() !== '#ffffff') style.backgroundColor = bg;
  }

  const align = cell.alignment || {};
  if (align.horizontal) style.textAlign = align.horizontal;
  if (align.vertical) {
    style.verticalAlign = align.vertical === 'center' ? 'middle' : align.vertical;
  }

  const border = cell.border || {};
  const top = borderToCss(border.top);
  const bottom = borderToCss(border.bottom);
  const left = borderToCss(border.left);
  const right = borderToCss(border.right);
  if (top) style.borderTop = top;
  if (bottom) style.borderBottom = bottom;
  if (left) style.borderLeft = left;
  if (right) style.borderRight = right;

  return style;
}

// ---------------------------------------------------------------------------
// Workbook -> render model
// ---------------------------------------------------------------------------

/** "B12" -> { col: 2, row: 12 }. */
function colRowFromAddr(addr) {
  const m = /^([A-Z]+)(\d+)$/.exec(addr);
  if (!m) return null;
  const letters = m[1];
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col = col * 26 + (letters.charCodeAt(i) - 64);
  }
  return { col, row: parseInt(m[2], 10) };
}

/** Build a render-friendly model of a worksheet. */
function buildSheetModel(ws, hf, sheetId, configRows, colHeaderMetricsMap, showDetailRows = true, hideEmptyRows = false) {
  // Merge map: every covered (row, col) -> covered-by-master; masters carry
  // the rowSpan / colSpan we need to emit.
  const merges = {};
  const mergesByMaster = {};
  const mergeRanges = (ws.model && ws.model.merges) ? ws.model.merges : [];
  for (const range of mergeRanges) {
    const [start, end] = range.split(':');
    if (!start || !end) continue;
    const s = colRowFromAddr(start);
    const e = colRowFromAddr(end);
    if (!s || !e) continue;
    mergesByMaster[`${s.row},${s.col}`] = {
      rowSpan: e.row - s.row + 1,
      colSpan: e.col - s.col + 1,
    };
    for (let r = s.row; r <= e.row; r++) {
      for (let c = s.col; c <= e.col; c++) {
        if (r === s.row && c === s.col) continue;
        merges[`${r},${c}`] = true;
      }
    }
  }

  let maxCol = ws.columnCount || 1;
  ws.eachRow({ includeEmpty: true }, (row) => {
    if (row.cellCount > maxCol) maxCol = row.cellCount;
  });

  const widths = [];
  for (let c = 1; c <= maxCol; c++) {
    const colDef = ws.getColumn(c);
    // ExcelJS width is in Excel character units; ~7px per unit is close enough.
    widths.push(colDef && colDef.width ? Math.round(colDef.width * 7) : null);
  }

  // Pre-build lookup maps for row labels (col B = col 2) and column headers.
  // The standard backend layout uses row 2 as header, but custom sheets ("Test",
  // hand-built tabs) may place headers on a different row. Detect it dynamically:
  // among the first 12 rows, pick the row whose cells most often look like
  // period labels (Q1 2023, FY 2024, 2023) or match a known period in the
  // backend's metrics map. Falls back to row 2.
  const LABEL_COL = 2; // column B
  const PERIOD_RX = /^(Q[1-4]|FY|H[12])\s*\d{2,4}$|^\d{4}$|^\d{4}-\d{2}-\d{2}$/i;
  const SEARCH_HEADER_ROWS = Math.min(12, ws.rowCount || 0);
  let HEADER_ROW = 2;
  let bestHeaderScore = -1;
  for (let r = 1; r <= SEARCH_HEADER_ROWS; r++) {
    const row = ws.getRow(r);
    let score = 0;
    for (let c = 3; c <= maxCol; c++) {
      const v = row.getCell(c).value;
      if (v === null || v === undefined || v === '') continue;
      const text = typeof v === 'object' && Array.isArray(v.richText)
        ? v.richText.map((rt) => rt.text).join('')
        : String(v).trim();
      if (!text) continue;
      if (PERIOD_RX.test(text)) score += 2;
      if (colHeaderMetricsMap && colHeaderMetricsMap[text]) score += 3;
    }
    if (score > bestHeaderScore) {
      bestHeaderScore = score;
      HEADER_ROW = r;
    }
  }

  const rowLabels = {};   // rowIndex -> display string
  const colHeaders = {};  // colIndex -> display string
  const rowCount = ws.rowCount || 0;
  for (let r = 1; r <= rowCount; r++) {
    const v = ws.getRow(r).getCell(LABEL_COL).value;
    if (v !== null && v !== undefined) {
      rowLabels[r] = typeof v === 'object' && Array.isArray(v.richText)
        ? v.richText.map((rt) => rt.text).join('')
        : String(v);
    }
  }
  const headerRow = ws.getRow(HEADER_ROW);
  for (let c = 1; c <= maxCol; c++) {
    const v = headerRow.getCell(c).value;
    if (v !== null && v !== undefined) {
      colHeaders[c] = typeof v === 'object' && Array.isArray(v.richText)
        ? v.richText.map((rt) => rt.text).join('')
        : String(v);
    }
  }

  // Build rows from model config order and use backend-computed values/formulas
  // Build a label -> config row lookup so the cell inspector can show the
  // sec tags / canonical key configured for each row.
  const cfgByLabel = {};
  if (Array.isArray(configRows)) {
    for (const rc of configRows) {
      if (rc && rc.label) cfgByLabel[rc.label] = rc;
    }
  }

  // Render directly from the company-specific worksheet that the backend
  // produced. Formulas are evaluated with HyperFormula so the UI shows the
  // recalculated value (the UI itself never edits/recalculates cells).
  const rows = [];
  for (let r = 1; r <= rowCount; r++) {
    const row = ws.getRow(r);
    if (row.hidden) continue;
    const cells = [];
    const rowLabel = rowLabels[r] || null;
    const rowCfg = rowLabel ? cfgByLabel[rowLabel] : null;
    const rowMode = rowCfg?.row_mode === 'details' ? 'details' : 'always';
    for (let c = 1; c <= maxCol; c++) {
      // Cells covered by a merge master render nothing.
      if (merges[`${r},${c}`]) {
        cells.push({ skip: true });
        continue;
      }
      const cell = row.getCell(c);
      const evalCtx = { hf, sheetId, row: r, col: c };
      const { display, isNumber, numericValue } = extractCellValue(cell, evalCtx);
      const rawVal = cell.value;
      const isFormula = rawVal && typeof rawVal === 'object' && 'formula' in rawVal;

      const colHeader = colHeaders[c] || null;
      const periodEntry = (colHeader && colHeaderMetricsMap)
        ? (colHeaderMetricsMap[colHeader] || null)
        : null;
      let secTags = rowCfg ? (rowCfg.sec_tags || []) : [];
      if (typeof secTags === 'string') secTags = [secTags];
      if (secTags.length === 0 && rowCfg && rowCfg.canonical_key) {
        secTags = [rowCfg.canonical_key];
      }

      const masterSpan = mergesByMaster[`${r},${c}`] || { rowSpan: 1, colSpan: 1 };
      const style = cellStyle(cell, isNumber, numericValue);
      if (rowCfg?.bold) style.fontWeight = 600;
      cells.push({
        display,
        style,
        rowSpan: masterSpan.rowSpan,
        colSpan: masterSpan.colSpan,
        debugInfo: {
          rowLabel,
          colHeader,
          formula: isFormula ? `=${rawVal.formula}` : null,
          rawValue: isFormula ? null : rawVal,
          computedDisplay: display,
          numFmt: cell.numFmt || null,
          secTags,
          canonicalKey: rowCfg ? rowCfg.canonical_key : null,
          rowMode,
          hideWhenEmpty: !!rowCfg?.hide_when_empty,
          periodMetrics: periodEntry ? periodEntry.metrics : null,
          periodEndDate: periodEntry ? periodEntry.period_end_date : null,
        },
      });
    }
    const hasData = cells.slice(2).some((cell) => {
      if (cell.skip) return false;
      return String(cell.display ?? '').trim() !== '';
    });
    if (rowMode === 'details' && !showDetailRows) {
      continue;
    }
    if (hideEmptyRows && rowCfg?.hide_when_empty && !hasData) {
      continue;
    }

    rows.push({ cells, height: row.height ? Math.round(row.height) : null, sourceRowIndex: r });
  }
  return { rows, widths, maxCol };
}

/**
 * Build the 2D array HyperFormula expects for one worksheet.
 * Formulas are passed as ``=...`` strings; other values are passed as-is.
 */
function buildSheetEvalData(ws) {
  let maxCol = ws.columnCount || 1;
  ws.eachRow({ includeEmpty: true }, (row) => {
    if (row.cellCount > maxCol) maxCol = row.cellCount;
  });
  const rowCount = ws.rowCount || 0;
  const data = [];
  for (let r = 1; r <= rowCount; r++) {
    const rowDef = ws.getRow(r);
    const arr = [];
    for (let c = 1; c <= maxCol; c++) {
      const v = rowDef.getCell(c).value;
      if (v === null || v === undefined) {
        arr.push(null);
      } else if (typeof v === 'object' && 'formula' in v) {
        arr.push(`=${v.formula}`);
      } else if (typeof v === 'object' && Array.isArray(v.richText)) {
        arr.push(v.richText.map((rt) => rt.text).join(''));
      } else if (v instanceof Date) {
        arr.push(v);
      } else if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') {
        arr.push(v);
      } else {
        arr.push(String(v));
      }
    }
    data.push(arr);
  }
  return data;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ResearchTablesTab({ companyId, earnings, onOpenModelConfig, onOpenSecTagNorm }) {
  const [definitions, setDefinitions] = useState([]);
  const [definitionId, setDefinitionId] = useState('default');
  const [loadingDefs, setLoadingDefs] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [includeForecast, setIncludeForecast] = useState(false);
  const [showDetailRows, setShowDetailRows] = useState(true);
  const [hideEmptyRows, setHideEmptyRows] = useState(false);
  const [scale, setScale] = useState('ones');
  const [sheets, setSheets] = useState([]); // [{ name, model }]
  const [activeSheet, setActiveSheet] = useState(0);
  const [inspected, setInspected] = useState(null); // { debugInfo, sheetName }
  const [companyConfig, setCompanyConfig] = useState(null);

  const hasData = !!(earnings && earnings.length);

  useEffect(() => {
    let cancelled = false;
    const loadCompanyConfig = async () => {
      if (!companyId) {
        setCompanyConfig(null);
        return;
      }
      try {
        const cfg = await (typeof researchService.getCompanyConfig === 'function'
          ? researchService.getCompanyConfig(companyId)
          : null);
        if (!cancelled) setCompanyConfig(cfg || null);
      } catch (_err) {
        if (!cancelled) setCompanyConfig(null);
      }
    };
    loadCompanyConfig();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const fiscalYearEndText = useMemo(() => {
    const month = companyConfig?.fiscal_year_end_month;
    const day = companyConfig?.fiscal_year_end_day;
    if (month == null || day == null) return null;
    return `Fiscal Year End ${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
  }, [companyConfig]);

  const hasFiscalOffset = useMemo(() => {
    const month = companyConfig?.fiscal_year_end_month;
    const day = companyConfig?.fiscal_year_end_day;
    if (month == null || day == null) return false;
    return Number(month) !== 12 || Number(day) !== 31;
  }, [companyConfig]);

  useEffect(() => {
    let cancelled = false;
    const loadDefinitions = async () => {
      setLoadingDefs(true);
      try {
        const defs = await researchService.listModelDefinitions();
        if (!cancelled && defs && defs.length) {
          setDefinitions(defs);
          // Use the is_default definition, or the first in the list.
          const defaultDef = defs.find(d => d.is_default) || defs[0];
          setDefinitionId((prev) => (defs.some((d) => d.id === prev) ? prev : defaultDef.id));
        }
      } catch (_err) {
        // Keep current selection if definitions fail to load.
      } finally {
        if (!cancelled) setLoadingDefs(false);
      }
    };
    loadDefinitions();
    return () => { cancelled = true; };
  }, []);

  const loadWorkbook = async () => {
    if (!companyId) return;
    setLoadingTable(true);
    setError(null);
    try {
      const [blob, cfgResp] = await Promise.all([
        researchService.fetchSpreadsheetBlob(
          companyId,
          'financial_model',
          definitionId,
          includeForecast ? 8 : 0,
          scale,
          hideEmptyRows,
        ),
        researchService.getGlobalModelConfig(definitionId).catch(() => null),
      ]);

      // Build label -> { sec_tags, canonical_key } from model config.
      // Config is keyed by sheet key (IS/BS/CFS/custom), then array of row defs.
      const labelConfigMap = {};
      if (cfgResp && cfgResp.config) {
        for (const rows of Object.values(cfgResp.config)) {
          if (!Array.isArray(rows)) continue;
          for (const row of rows) {
            const lbl = row.label;
            if (lbl) {
              let secTags = row.sec_tags || [];
              if (typeof secTags === 'string') secTags = [secTags];
              // If config is SEC-tag-centric, ensure secTags always contains the canonical_key if not present
              if (secTags.length === 0 && row.canonical_key) {
                secTags = [row.canonical_key];
              }
              labelConfigMap[lbl] = {
                canonical_key: row.canonical_key || null,
                sec_tags: secTags,
              };
            }
          }
        }
      }

      // Build period-label -> metrics from earnings (quarterly data).
      // The backend labels quarters as "Q{fq} {fy}" so we replicate that.
      const colHeaderMetricsMap = {};
      if (earnings && earnings.length) {
        for (const row of earnings) {
          const fy = row.fiscal_year;
          const fq = row.fiscal_quarter;
          const qs = row.quarter || '';
          let label;
          if (fy && fq) {
            label = `Q${fq} ${fy}`;
          } else if (qs) {
            label = qs.replace('-', ' ');
          } else {
            label = row.period_end_date || '';
          }
          if (label) colHeaderMetricsMap[label] = { metrics: row.metrics || {}, period_end_date: row.period_end_date || null };
        }
      }

      const buffer = await blob.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buffer);

      // Build a HyperFormula instance over every sheet so cross-sheet
      // formulas (=SUM('Income Statement'!C3:F3) etc.) resolve to real
      // numbers before we render.
      const sheetsData = {};
      wb.worksheets.forEach((ws) => {
        sheetsData[ws.name] = buildSheetEvalData(ws);
      });
      let hf = null;
      try {
        hf = HyperFormula.buildFromSheets(sheetsData, { licenseKey: 'gpl-v3' });
      } catch (_err) {
        hf = null;
      }

      const parsed = wb.worksheets.map((ws) => {
        const sheetId = hf ? hf.getSheetId(ws.name) : null;
        // Find config array for this worksheet
        let sheetKey = cfgResp && cfgResp.config ? Object.keys(cfgResp.config).find(k => ws.name.toLowerCase().includes(k.toLowerCase())) : null;
        if (!sheetKey && cfgResp && cfgResp.config) sheetKey = Object.keys(cfgResp.config)[0];
        const configRows = (cfgResp && cfgResp.config && sheetKey) ? cfgResp.config[sheetKey] : [];
        return {
          name: ws.name,
          model: buildSheetModel(ws, hf, sheetId, configRows, colHeaderMetricsMap, showDetailRows, hideEmptyRows),
        };
      });

      if (hf) {
        try { hf.destroy(); } catch (_err) { /* noop */ }
      }
      setSheets(parsed);
      setActiveSheet(0);
    } catch (err) {
      setSheets([]);
      setError(err?.response?.data?.detail || err.message || 'Failed to load spreadsheet preview');
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    loadWorkbook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, definitionId, includeForecast, scale, showDetailRows, hideEmptyRows]);

  const handleDownload = async () => {
    if (!companyId) return;
    setDownloading(true);
    setError(null);
    try {
      await researchService.downloadSpreadsheet(
        companyId,
        'financial_model',
        definitionId,
        includeForecast ? 8 : 0,
        scale,
        hideEmptyRows,
      );
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const active = sheets[activeSheet]?.model;

  const colgroup = useMemo(() => {
    if (!active) return null;
    return (
      <colgroup>
        {/* row-number gutter */}
        <col style={{ width: 44 }} />
        {active.widths.map((w, i) => (
          <col key={i} style={w ? { width: w } : undefined} />
        ))}
      </colgroup>
    );
  }, [active]);

  return (
    <div className="p-3">
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <label className="form-label mb-0 fw-semibold">Spreadsheet:</label>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto', minWidth: 220 }}
          value={definitionId}
          onChange={(e) => setDefinitionId(e.target.value)}
          disabled={loadingDefs || loadingTable}
        >
          {definitions.map((d) => (
            <option key={d.id} value={d.id}>{d.is_default ? '★ ' : ''}{d.name}</option>
          ))}
        </select>
        <label className="form-label mb-0 fw-semibold">Scale:</label>
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto', minWidth: 140 }}
          value={scale}
          onChange={(e) => setScale(e.target.value)}
          disabled={loadingDefs || loadingTable}
        >
          <option value="ones">Ones</option>
          <option value="thousands">Thousands</option>
          <option value="millions">Millions</option>
          <option value="billions">Billions</option>
        </select>
        {fiscalYearEndText && (
          <span
            className={`badge ${hasFiscalOffset ? 'text-bg-info' : 'text-bg-secondary'}`}
            title={
              hasFiscalOffset
                ? 'Fiscal quarters are offset from calendar quarters for this company.'
                : 'Fiscal year aligns with calendar year end.'
            }
          >
            {fiscalYearEndText}
          </span>
        )}
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="rtt-detail-toggle"
            checked={showDetailRows}
            onChange={e => setShowDetailRows(e.target.checked)}
          />
          <label className="form-check-label small text-muted" htmlFor="rtt-detail-toggle">
            Show detail lines
          </label>
        </div>
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="rtt-hide-empty-rows-toggle"
            checked={hideEmptyRows}
            onChange={e => setHideEmptyRows(e.target.checked)}
            disabled={loadingTable}
          />
          <label className="form-check-label small text-muted" htmlFor="rtt-hide-empty-rows-toggle">
            Hide empty rows
          </label>
        </div>
        {onOpenModelConfig && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onOpenModelConfig()}
            title="Configure spreadsheet model (row labels, SEC tags)"
          >
            <i className="bi bi-sliders me-1" />Model Config
          </button>
        )}
        {onOpenSecTagNorm && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onOpenSecTagNorm()}
            title="Configure XBRL tag → normalized column mapping"
          >
            <i className="bi bi-tags me-1" />SEC Tag Normalization
          </button>
        )}
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={loadWorkbook}
          disabled={loadingTable}
          title="Reload spreadsheet preview"
        >
          {loadingTable
            ? <span className="spinner-border spinner-border-sm me-1" />
            : <i className="bi bi-arrow-clockwise me-1" />}
          {loadingTable ? 'Loading…' : 'Refresh Preview'}
        </button>
        <div className="ms-auto">
          <button
            className="btn btn-sm btn-outline-success"
            onClick={handleDownload}
            disabled={downloading || !hasData}
            title={hasData ? 'Download this spreadsheet as Excel' : 'No earnings data to export'}
          >
            {downloading
              ? <span className="spinner-border spinner-border-sm me-1" />
              : <i className="bi bi-download me-1" />}
            {downloading ? 'Generating…' : 'Download Excel'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {String(error)}
        </div>
      )}

      {!loadingTable && (
        <div className="d-flex align-items-center mb-2" style={{ gap: 6 }}>
          <ul className="nav nav-pills nav-sm mb-0 me-auto" style={{ gap: 6 }}>
            {sheets.map((s, i) => (
              <li className="nav-item" key={s.name}>
                <button
                  className={`nav-link py-1 px-2 ${activeSheet === i ? 'active' : ''}`}
                  onClick={() => setActiveSheet(i)}
                  style={{ fontSize: 12 }}
                >
                  {s.name}
                </button>
              </li>
            ))}
          </ul>
          <div className="form-check mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="rtt-forecast-toggle"
              checked={includeForecast}
              onChange={e => setIncludeForecast(e.target.checked)}
            />
            <label className="form-check-label small text-muted" htmlFor="rtt-forecast-toggle">
              +8Q estimate columns
            </label>
          </div>
        </div>
      )}

      {loadingTable ? (
        <div className="text-muted small">
          <span className="spinner-border spinner-border-sm me-2" />Building table preview…
        </div>
      ) : !active ? (
        <div className="text-muted small">No spreadsheet data available yet.</div>
      ) : (
        <>
          <div
            className="table-responsive"
            style={{ maxHeight: 640, overflow: 'auto', border: '1px solid #dee2e6' }}
          >
            <table
              className="mb-0"
              style={{ borderCollapse: 'collapse', fontSize: 12, tableLayout: 'auto', width: 'auto' }}
            >
              {colgroup}
              <tbody>
                {active.rows.map((row, rIdx) => (
                  <tr key={rIdx} style={row.height ? { height: row.height * 1.33 } : undefined}>
                    <th
                      className="text-muted"
                      style={{
                        width: 44, minWidth: 44, padding: '2px 6px',
                        textAlign: 'right', fontWeight: 400,
                        background: '#f8f9fa',
                        borderRight: '1px solid #dee2e6',
                        borderBottom: '1px solid #f1f3f5',
                        position: 'sticky',
                        left: 0,
                        top: rIdx === 0 ? 0 : undefined,
                        zIndex: rIdx === 0 ? 5 : 3,
                      }}
                    >
                      {row.sourceRowIndex ?? (rIdx + 1)}
                    </th>
                    {row.cells.map((cell, cIdx) => {
                      if (cell.skip) return null;
                      const isTopRow = rIdx === 0;
                      const isLockedSheetCol = cIdx === 1;
                      const stickyStyle = {
                        ...(isTopRow ? { top: 0 } : null),
                        ...(isLockedSheetCol ? { left: 44 } : null),
                      };

                      return (
                        <td
                          key={cIdx}
                          rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                          colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                          style={{
                            ...cell.style,
                            cursor: 'default',
                            ...(isTopRow || isLockedSheetCol ? {
                              position: 'sticky',
                              zIndex: isTopRow && isLockedSheetCol ? 4 : (isTopRow ? 3 : 2),
                              background: cell.style?.backgroundColor || '#ffffff',
                            } : null),
                            ...stickyStyle,
                          }}
                          title="Double-click to inspect"
                          onDoubleClick={() => setInspected({
                            debugInfo: cell.debugInfo,
                            sheetName: sheets[activeSheet]?.name,
                          })}
                        >
                          {cell.display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-muted small mt-1 mb-0" style={{ fontSize: 11 }}>
            Double-click any cell to inspect its value and formula.
          </p>
        </>
      )}

      {/* Cell inspector panel */}
      {inspected && (
        <CellInspector
          info={inspected}
          onClose={() => setInspected(null)}
          companyId={companyId}
          definitionId={definitionId}
          onOpenModelConfig={onOpenModelConfig}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell Inspector panel
// ---------------------------------------------------------------------------

function CellInspector({ info, onClose, companyId, definitionId, onOpenModelConfig }) {
  const { debugInfo, sheetName } = info;
  const d = debugInfo || {};

  const rawDisplay = (() => {
    const rv = d.rawValue;
    if (rv === null || rv === undefined) return '—';
    if (typeof rv === 'number') return rv.toLocaleString();
    if (rv instanceof Date) return rv.toISOString().slice(0, 10);
    if (typeof rv === 'object') return JSON.stringify(rv);
    return String(rv);
  })();

  // Resolve canonicalVal using mapped secTags, matching table logic.
  // Always resolve value using secTags from model config only
  const canonicalVal = (() => {
    const metrics = d.periodMetrics;
    const secTags = d.secTags || [];
    if (!metrics) return null;
    for (const tag of secTags) {
      const v = metrics[tag];
      if (v !== null && v !== undefined && v !== '') return v;
    }
    return null;
  })();

  const secTags = d.secTags || [];
  const metricsMap = d.periodMetrics || {};

  // Value for a configured tag straight from the stored period metrics — the
  // same source the backend now uses to populate the cell (the live XBRL fetch
  // was removed from the export path).  Mirrors the backend's tag-variant
  // fallback (PascalCase tag + snake_case variants).
  const camelToSnake = (s) =>
    String(s)
      .replace(/(.)([A-Z][a-z]+)/g, '$1_$2')
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase();
  const metricTagValue = (tag) => {
    if (!tag) return null;
    const variants = [tag, tag.toLowerCase(), camelToSnake(tag)];
    for (const v of variants) {
      const mv = metricsMap[v];
      if (mv !== null && mv !== undefined && mv !== '') return mv;
    }
    return null;
  };

  // For multi-tag rows, query SEC EDGAR to find which specific tag had a value.
  const [secTagValues, setSecTagValues] = useState(null); // null = not yet loaded
  const [secTagLoading, setSecTagLoading] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null); // null = not triggered yet

  useEffect(() => {
    if (secTags.length <= 1 || !d.periodEndDate || !companyId) {
      setSecTagValues(null);
      return;
    }
    let cancelled = false;
    setSecTagLoading(true);
    researchService.getSecTagValues(companyId, secTags, d.periodEndDate)
      .then((result) => {
        if (!cancelled) setSecTagValues(result);
      })
      .catch(() => {
        if (!cancelled) setSecTagValues(null);
      })
      .finally(() => {
        if (!cancelled) setSecTagLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, d.periodEndDate, secTags.join(',')]);

  // Decide effective value for a tag: stored metrics first (what the backend
  // actually used), then SEC lookup result, else canonical fallback.
  const tagHasValue = (tag) => {
    if (metricTagValue(tag) !== null) return true;
    if (secTagValues) return secTagValues[tag] !== null && secTagValues[tag] !== undefined;
    // Single-tag: canonical key value applies unambiguously.
    if (secTags.length === 1) return canonicalVal !== null;
    return false; // multi-tag, not yet resolved
  };
  const tagValue = (tag) => {
    const mv = metricTagValue(tag);
    if (mv !== null) return mv;
    if (secTagValues) return secTagValues[tag] ?? null;
    if (secTags.length === 1) return canonicalVal;
    return null;
  };

  // First tag with a value (in array order) is the one actually used by the
  // backend.  The backend resolves from the first configured tag present in the
  // stored period metrics, so check that first; fall back to the live SEC
  // lookup only if no metric matched.  Subsequent tags with values are present
  // in SEC but were not used.
  const firstUsedIdx = (() => {
    const metricIdx = secTags.findIndex((tag) => metricTagValue(tag) !== null);
    if (metricIdx !== -1) return metricIdx;
    if (secTagValues) {
      return secTags.findIndex((tag) => secTagValues[tag] !== null && secTagValues[tag] !== undefined);
    }
    return secTags.length === 1 && canonicalVal !== null ? 0 : -1;
  })();

  // All configured tags returned no value (after lookup completes).  A tag
  // counts as having a value if either the stored metrics or the live SEC
  // lookup produced one, so a successful metrics resolution suppresses the
  // "missing" state even when the live SEC query fails.
  const allTagsMissing = !secTagLoading && secTags.length > 0
    && (secTagValues !== null || secTags.every((tag) => metricTagValue(tag) === null))
    && secTags.every((tag) => !tagHasValue(tag));

  // Single-tag row where the tag has no value for this period (secTagValues is never
  // fetched for single-tag rows, so we derive missing-ness from canonicalVal directly).
  const singleTagMissing = secTags.length === 1 && canonicalVal === null;

  // Cell has no data from any source — show AI suggest as an exploratory action.
  // Includes the case where no XBRL tags are configured at all.
  const cellIsEmpty = (d.computedDisplay === null || d.computedDisplay === undefined || d.computedDisplay === '' || d.computedDisplay === '—')
    && (d.rawValue === null || d.rawValue === undefined || d.rawValue === '');
  const showSuggestButton = !!companyId && !!d.canonicalKey
    && (allTagsMissing || singleTagMissing || (secTags.length === 0 && cellIsEmpty));

  const handleSuggest = () => {
    if (!companyId || !d.canonicalKey) return;
    setSuggestLoading(true);
    setSuggestions(null);
    researchService.suggestSecTags(companyId, {
      canonical_key: d.canonicalKey,
      row_label: d.rowLabel || d.canonicalKey,
      period_end_date: d.periodEndDate || null,
      existing_tags: secTags,
    })
      .then((result) => setSuggestions(result.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setSuggestLoading(false));
  };

  const handleAddTag = (tag) => {
    if (!d.canonicalKey || !tag || !onOpenModelConfig) return;
    // Open the model config dialog focused on the row, with the tag pre-staged.
    // The user reviews + saves manually inside the dialog.
    onOpenModelConfig({
      definitionId: definitionId || undefined,
      canonicalKey: d.canonicalKey,
      addTag: tag,
    });
    onClose();
  };

  // 'used' | 'present' (has value but not used) | 'none'
  const tagStatus = (tag, idx) => {
    if (!tagHasValue(tag)) return 'none';
    if (idx === firstUsedIdx) return 'used';
    return 'present';
  };

  return (
    <>
      {/* Backdrop */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1040 }} onClick={onClose} />
      <div
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1050,
          width: 420, maxWidth: 'calc(100vw - 32px)',
          background: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: 8,
          boxShadow: '0 4px 24px rgba(0,0,0,.15)',
          fontSize: 13,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px',
            background: '#212529', color: '#fff',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <span style={{ fontWeight: 600 }}>
            <i className="bi bi-search me-1" />Cell Inspector
          </span>
          <button className="btn-close btn-close-white" style={{ fontSize: 10 }} onClick={onClose} />
        </div>

        {/* Body */}
        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '70vh', overflowY: 'auto' }}>

          <div style={{ color: '#6c757d', fontSize: 11 }}>
            Sheet: <strong style={{ color: '#212529' }}>{sheetName}</strong>
          </div>

          <Row label="Row (metric)" value={d.rowLabel || '—'} />
          <Row label="Column (period)" value={d.colHeader || '—'} />
          {d.canonicalKey && <Row label="Canonical key" value={d.canonicalKey} mono />}
          <Row label="Row mode" value={(d.rowMode || 'always') === 'details' ? 'Details' : 'Standard'} />
          <Row label="Hide when empty" value={d.hideWhenEmpty ? 'Yes' : 'No'} />

          <hr style={{ margin: '4px 0' }} />

          <Row label="Displayed value" value={d.computedDisplay || '—'} mono />
          <Row label="Raw / cached value" value={rawDisplay} mono />
          {d.numFmt && <Row label="Number format" value={d.numFmt} mono />}

          {/* Formula */}
          {d.formula ? (
            <div>
              <div style={{ color: '#6c757d', fontSize: 11, marginBottom: 3 }}>Formula</div>
              <pre
                style={{
                  margin: 0, padding: '6px 8px',
                  background: '#f8f9fa', borderRadius: 4,
                  fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  border: '1px solid #e9ecef',
                  maxHeight: 120, overflowY: 'auto',
                }}
              >
                {'=' + d.formula}
              </pre>
            </div>
          ) : (
            <Row label="Formula" value="None — static value" />
          )}

          {/* SEC Tags */}
          {(secTags.length > 0 || d.canonicalKey) && (
            <>
              <hr style={{ margin: '4px 0' }} />
              <div style={{ color: '#6c757d', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
                SEC / Metric Tags
              </div>

              {/* Period value from canonical key */}
              {d.canonicalKey && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ color: '#6c757d', fontSize: 11, minWidth: 130 }}>Period data</span>
                  {canonicalVal !== null
                    ? <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600, color: '#198754' }}>
                      {typeof canonicalVal === 'number' ? canonicalVal.toLocaleString() : String(canonicalVal)}
                    </span>
                    : <span style={{ fontSize: 11, color: '#adb5bd' }}>no value</span>
                  }
                  <span style={{ fontSize: 10, color: '#6c757d', marginLeft: 4 }}>
                    (key: <code style={{ fontSize: 10 }}>{d.canonicalKey}</code>)
                  </span>
                </div>
              )}

              {/* Configured XBRL tags */}
              {secTags.length > 0 && (
                <>
                  {secTags.length > 1 && (
                    <div style={{ color: '#6c757d', fontSize: 10, marginBottom: 3 }}>
                      Configured XBRL tags <span style={{ fontStyle: 'italic' }}>(alternatives — only one is filed per company)</span>:
                      {secTagLoading && <span className="spinner-border spinner-border-sm ms-2" style={{ width: 10, height: 10 }} />}
                    </div>
                  )}
                  {secTags.map((tag, idx) => {
                    const status = tagStatus(tag, idx);
                    const val = tagValue(tag);
                    const bgColor = status === 'used' ? '#f0fff4' : status === 'present' ? '#fffbe6' : '#fafafa';
                    const borderColor = status === 'used' ? '#b7ebc8' : status === 'present' ? '#ffe58f' : '#e9ecef';
                    const textColor = status === 'used' ? '#212529' : status === 'present' ? '#7d5a00' : '#6c757d';
                    return (
                      <div
                        key={tag}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '3px 6px', borderRadius: 4,
                          background: bgColor,
                          border: `1px solid ${borderColor}`,
                          marginBottom: 2,
                        }}
                      >
                        {status === 'used'
                          ? <i className="bi bi-check-circle-fill" style={{ color: '#198754', fontSize: 12 }} />
                          : status === 'present'
                            ? <i className="bi bi-exclamation-circle-fill" style={{ color: '#d4a017', fontSize: 12 }} />
                            : <i className="bi bi-dash-circle" style={{ color: '#adb5bd', fontSize: 12 }} />}
                        <span style={{ fontFamily: 'monospace', fontSize: 11, color: textColor }}>
                          {tag}
                        </span>
                        {val !== null && (
                          <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 11, color: status === 'used' ? '#0d6efd' : '#b8860b' }}>
                            {typeof val === 'number' ? val.toLocaleString() : String(val)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* AI suggest button — shown when no SEC value was found, regardless of whether tags are configured */}
              {showSuggestButton && suggestions === null && (
                <div style={{ marginTop: 6 }}>
                  <button
                    className="btn btn-sm"
                    style={{
                      fontSize: 11, padding: '3px 10px',
                      background: '#f0f0ff', border: '1px solid #b0b0f0',
                      color: '#3c3c8c', borderRadius: 4,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                    onClick={handleSuggest}
                    disabled={suggestLoading}
                  >
                    {suggestLoading
                      ? <><span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} /> Searching SEC + AI…</>
                      : <><i className="bi bi-stars" /> Find tags with AI</>}
                  </button>
                  {!d.periodEndDate && (
                    <div style={{ fontSize: 10, color: '#6c757d', marginTop: 3, fontStyle: 'italic' }}>
                      No period set — will search latest available filings.
                    </div>
                  )}
                </div>
              )}

              {/* AI suggestion results */}
              {suggestions !== null && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, color: '#5c5ca0', fontWeight: 600, marginBottom: 4 }}>
                    <i className="bi bi-stars me-1" />AI-suggested XBRL tags{d.periodEndDate ? ' for this period' : ''}:
                  </div>
                  {suggestions.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#6c757d', fontStyle: 'italic' }}>
                      No alternative tags found.
                    </div>
                  ) : suggestions.map((s) => {
                    return (
                      <div
                        key={s.tag}
                        style={{
                          display: 'flex', flexDirection: 'column', gap: 2,
                          padding: '4px 6px', borderRadius: 4,
                          background: '#f0f0ff',
                          border: '1px solid #c0c0f0',
                          marginBottom: 4,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i className="bi bi-lightbulb-fill" style={{ color: '#5c5ca0', fontSize: 11 }} />
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#3c3c8c', flex: 1 }}>
                            {s.tag}
                          </span>
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#5c5ca0', fontWeight: 600 }}>
                            {typeof s.value === 'number' ? s.value.toLocaleString() : String(s.value)}
                          </span>
                          {d.canonicalKey && onOpenModelConfig && (
                            <button
                              className="btn btn-sm"
                              title="Open model config and stage this tag for the metric"
                              onClick={() => handleAddTag(s.tag)}
                              style={{
                                fontSize: 10, padding: '0 6px', lineHeight: '16px',
                                background: '#fff',
                                border: '1px solid #5c5ca0',
                                color: '#3c3c8c',
                                borderRadius: 3, marginLeft: 4,
                              }}
                            >
                              <i className="bi bi-pencil-square" /> Edit
                            </button>
                          )}
                        </div>
                        {s.period_end && !d.periodEndDate && (
                          <div style={{ fontSize: 10, color: '#6c757d', paddingLeft: 18 }}>
                            Period ending {s.period_end}
                          </div>
                        )}
                        {s.rationale && (
                          <div style={{ fontSize: 10, color: '#6c757d', paddingLeft: 18 }}>
                            {s.rationale}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <span style={{ color: '#6c757d', fontSize: 11, flexShrink: 0, width: 130 }}>{label}</span>
      <span style={mono ? { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' } : { fontSize: 13 }}>
        {value}
      </span>
    </div>
  );
}
